// Renewal Pipeline section — FE-035 / spec §7.0.6 + Dashboard-Filters-01.
//
// Two widget-level filters:
//   - Months: 3 / 6 / 12 (passed to BE)
//   - Renewal status chips: on_track / behind / at_risk / not_started
//     (client-side filter over the returned buckets; the BE always
//     returns every status per month).

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  buildAccountFilterUrl,
  DashboardCard,
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
  useRenewalPipelineQuery,
  type RenewalPipelineBucket,
  type RenewalPipelineResponse,
} from "@/features/dashboard";

import { RenewalLegend } from "./RenewalLegend";
import { RenewalPipelineChart } from "./RenewalPipelineChart";


export const RENEWAL_PIPELINE_SECTION = "renewal-pipeline" as const;
export const RENEWAL_PIPELINE_SPAN_CLASS = "xl:col-span-2 md:col-span-2";


type MonthsOption = 3 | 6 | 12;
const MONTHS_OPTIONS: MonthsOption[] = [3, 6, 12];
const DEFAULT_MONTHS: MonthsOption = 6;

type RenewalStatus = "on_track" | "behind" | "at_risk" | "not_started";
const RENEWAL_STATUSES: Array<{ value: RenewalStatus; label: string; chip: string }> = [
  { value: "on_track", label: "On track", chip: "bg-sage-tint text-teal" },
  { value: "behind", label: "Behind", chip: "bg-gold-tint text-gold" },
  { value: "at_risk", label: "At risk", chip: "bg-ember-tint text-ember" },
  { value: "not_started", label: "Not started", chip: "bg-recessed text-text-secondary" },
];


export function RenewalPipelineSection() {
  const [months, setMonths] = useState<MonthsOption>(DEFAULT_MONTHS);
  const [activeStatuses, setActiveStatuses] = useState<RenewalStatus[]>([]);
  const query = useRenewalPipelineQuery({ months });

  // Apply status filter client-side: zero-out the non-selected status
  // bands in each bucket so the chart re-stacks. If nothing is
  // selected, no filter is applied.
  const filteredBuckets = useMemo<RenewalPipelineBucket[]>(() => {
    if (activeStatuses.length === 0) return query.data?.buckets ?? [];
    const active = new Set(activeStatuses);
    return (query.data?.buckets ?? []).map((b) => ({
      month: b.month,
      on_track: active.has("on_track") ? b.on_track : 0,
      behind: active.has("behind") ? b.behind : 0,
      at_risk: active.has("at_risk") ? b.at_risk : 0,
      not_started: active.has("not_started") ? b.not_started : 0,
      total:
        (active.has("on_track") ? b.on_track : 0)
        + (active.has("behind") ? b.behind : 0)
        + (active.has("at_risk") ? b.at_risk : 0)
        + (active.has("not_started") ? b.not_started : 0),
    }));
  }, [query.data, activeStatuses]);

  function toggleStatus(s: RenewalStatus): void {
    setActiveStatuses((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
  }

  return (
    <DashboardCard
      section={RENEWAL_PIPELINE_SECTION}
      className={RENEWAL_PIPELINE_SPAN_CLASS}
      title="Renewal pipeline"
      subtitle="Upcoming renewals by readiness for the next few months."
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <RenewalStatusChips
            value={activeStatuses}
            onToggle={toggleStatus}
          />
          <MonthsSelector value={months} onChange={setMonths} />
        </div>
      }
    >
      {query.isLoading ? (
        <DashboardLoadingState variant="card" />
      ) : query.isError ? (
        <DashboardErrorState
          title="Couldn't load Renewal pipeline"
          retry={() => query.refetch()}
        />
      ) : query.data && hasAnyRenewal({ ...query.data, buckets: filteredBuckets }) ? (
        <div className="flex flex-col gap-4">
          <AttentionLine data={query.data} />
          <RenewalPipelineChart buckets={filteredBuckets} />
          <RenewalLegend />
        </div>
      ) : (
        <DashboardEmptyState
          title="No upcoming renewals in this window"
          description={
            "Once Accounts have PlanYears with renewal dates in the next " +
            `${months} months, the pipeline appears here.`
          }
        />
      )}
    </DashboardCard>
  );
}


function hasAnyRenewal(data: RenewalPipelineResponse): boolean {
  return data.buckets.some((b) => b.total > 0);
}


function AttentionLine({ data }: { data: RenewalPipelineResponse }) {
  const count = data.attention_count;
  const text = data.subtitle || `${count} renewals need attention`;
  if (count === 0) {
    return (
      <p
        data-testid="renewal-attention-line"
        className="text-sm text-text-secondary"
      >
        Every renewal in the window is on track.
      </p>
    );
  }
  return (
    <p
      data-testid="renewal-attention-line"
      className="text-sm text-text-primary"
    >
      <Link
        to={buildAccountFilterUrl({ renewal_attention: true })}
        data-testid="renewal-attention-link"
        className="font-semibold text-ember hover:underline"
      >
        {text}
      </Link>
      <span className="ml-1 text-text-secondary">
        — behind, at-risk, or not started.
      </span>
    </p>
  );
}


function RenewalStatusChips({
  value, onToggle,
}: { value: RenewalStatus[]; onToggle: (s: RenewalStatus) => void }) {
  const set = new Set(value);
  return (
    <div
      data-testid="renewal-status-chips"
      data-count={value.length}
      role="group"
      aria-label="Renewal status filter"
      className="inline-flex items-center gap-1 rounded-md border border-border-subtle bg-raised p-1"
    >
      {RENEWAL_STATUSES.map((opt) => {
        const active = set.has(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={active}
            data-testid={`renewal-status-${opt.value}`}
            data-active={active ? "true" : "false"}
            onClick={() => onToggle(opt.value)}
            className={[
              "rounded px-1.5 py-0.5 text-[11px] font-medium transition-fast ease-out",
              active
                ? opt.chip
                : "text-text-secondary hover:text-text-primary",
            ].join(" ")}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}


function MonthsSelector({
  value, onChange,
}: { value: MonthsOption; onChange: (next: MonthsOption) => void }) {
  return (
    <div
      data-testid="renewal-months-selector"
      role="group"
      aria-label="Renewal pipeline horizon"
      className="inline-flex items-center gap-1 rounded-md border border-border-subtle bg-raised p-1"
    >
      {MONTHS_OPTIONS.map((opt) => {
        const active = opt === value;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            aria-pressed={active}
            data-active={active ? "true" : "false"}
            data-testid={`renewal-months-${opt}`}
            className={[
              "rounded px-2.5 py-1 text-xs font-medium transition-fast ease-out",
              active
                ? "bg-teal-tint text-text-primary"
                : "text-text-secondary hover:text-text-primary",
            ].join(" ")}
          >
            {opt} mo
          </button>
        );
      })}
    </div>
  );
}
